import Alpine from 'alpinejs';
import flatpickr from 'flatpickr';
import { getCSRFToken } from '../utils/getCSRFToken';

const PRICE_MAX = 99999.99;

const FIELD_DISPLAY_ORDER = [
  'availability',
  'price',
  'min_length',
  'max_length',
  'closed_to_arrival',
  'closed_to_departure',
  'instant_book_enabled',
  'is_blocked',
];

function parseNumeric(value) {
  if (value === '' || value === null || typeof value === 'undefined') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function clampPriceValue(value) {
  if (value === null || typeof value === 'undefined') {
    return null;
  }
  return Math.min(value, PRICE_MAX);
}

function formatDateISOCalendar(dateObj) {
  if (!(dateObj instanceof Date)) {
    return '';
  }
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function registerCalendarSettingsPricingConditionsStore() {
  const existing = Alpine.store('calendarSettingsPricingConditionsStore');
  if (existing) return existing;

  const baseStore = Alpine.store('calendarSettingsStore');
  if (!baseStore) return null;

  const store = {};
  Object.defineProperties(store, Object.getOwnPropertyDescriptors(baseStore));

  const extension = {
    priceMax: PRICE_MAX,
    bookingMode: '',
    loading: false,
    currentDate: '',
    days: 14,
    navigationStepDays: 7,
    areFiltersCollapsed: true,
    dates: [],
    fieldOptions: [],
    selectedFields: ['price', 'availability'],
    pendingSelectedFields: ['price', 'availability'],
    selectedRateSetIds: [],
    pendingSelectedRateSetIds: [],
    ratesetSearch: '',
    rateSetOptions: [],
    allRateSetOptions: [],
    flexibleRatesets: [],
    fixedRatesets: [],
    flexibleGroups: [],
    fixedGroups: [],
    flexibleDisplayRows: [],
    fixedDisplayRows: [],
    newFixedIntervalByRateSetId: {},
    _fixedIntervalPickerInstances: {},
    changedCells: {},
    bulkSelection: [],
    renderVersion: 0,
    loadRequestId: 0,
    startDatePickerInstance: null,
    bulkDateRangePickerInstance: null,
    bulkStatusPollHandle: null,
    bulkStatusHideHandle: null,
    tableScrollerElement: null,
    flexibleHeaderStickyElement: null,
    flexibleHeaderTableElement: null,
    _flexibleHeaderWheelListenerAttached: false,
    _pricingTableWheelHandler: null,
    _pricingTableResizeHandlerRegistered: false,
    _beforeUnloadHandler: null,
    _beforeUnloadHandlerRegistered: false,
    showTableScrollHintLeft: false,
    showTableScrollHintRight: false,
    showBulkModal: false,
    showBulkStatusModal: false,
    bulkFormSubmitError: '',
    bulkUpdateStatus: {
      type: 'info',
      message: '',
      inProgress: false,
    },
    bulkForm: {
      start_date: '',
      end_date: '',
      price: '',
      prices: [],
      price_active: false,
      min_length: '',
      min_length_active: false,
      max_length: '',
      max_length_active: false,
      closed_to_arrival: [],
      closed_to_arrival_active: false,
      closed_to_departure: [],
      closed_to_departure_active: false,
      instant_book_enabled: [],
      instant_book_enabled_active: false,
      is_blocked: false,
      is_blocked_active: true,
    },
    get hasChanges() {
      return Object.values(this.changedCells).some(
        (change) => Object.keys(change?.fields || {}).length > 0,
      );
    },
    get hasPendingFieldFilterChanges() {
      const selected = Array.from(new Set(this.selectedFields || [])).sort();
      const pending = Array.from(
        new Set(this.pendingSelectedFields || []),
      ).sort();
      if (selected.length !== pending.length) {
        return true;
      }
      return selected.some((value, index) => value !== pending[index]);
    },
    get hasPendingRateSetFilterChanges() {
      const selected = Array.from(
        new Set(this.selectedRateSetIds || []),
      ).sort();
      const pending = Array.from(
        new Set(this.pendingSelectedRateSetIds || []),
      ).sort();
      if (selected.length !== pending.length) {
        return true;
      }
      return selected.some((value, index) => value !== pending[index]);
    },
    get hasPendingFilterChanges() {
      return (
        this.hasPendingFieldFilterChanges || this.hasPendingRateSetFilterChanges
      );
    },
    setBulkFormSubmitError(message) {
      this.bulkFormSubmitError = message || '';
    },
    setBulkUpdateStatus(type, message, inProgress = false) {
      if (this.bulkStatusHideHandle) {
        clearTimeout(this.bulkStatusHideHandle);
        this.bulkStatusHideHandle = null;
      }
      this.bulkUpdateStatus = {
        type,
        message,
        inProgress,
      };
      this.showBulkStatusModal = true;
    },
    hideBulkUpdateStatus(delayMs = 0) {
      if (this.bulkUpdateStatus.inProgress && !delayMs) {
        return;
      }
      if (this.bulkStatusHideHandle) {
        clearTimeout(this.bulkStatusHideHandle);
        this.bulkStatusHideHandle = null;
      }
      const hideNow = () => {
        this.showBulkStatusModal = false;
        this.bulkUpdateStatus = {
          type: 'info',
          message: '',
          inProgress: false,
        };
      };
      if (!delayMs || delayMs <= 0) {
        hideNow();
        return;
      }
      this.bulkStatusHideHandle = setTimeout(() => {
        hideNow();
        this.bulkStatusHideHandle = null;
      }, delayMs);
    },
    stopBulkStatusPolling() {
      if (this.bulkStatusPollHandle) {
        clearTimeout(this.bulkStatusPollHandle);
        this.bulkStatusPollHandle = null;
      }
    },
    async pollBulkUpdateTasks(taskIds) {
      if (!Array.isArray(taskIds) || taskIds.length === 0) {
        return;
      }
      try {
        const responses = await Promise.all(
          taskIds.map((taskId) =>
            fetch(`/rates/bulk-update-status/${taskId}/`).then((response) =>
              response.json(),
            ),
          ),
        );
        const completedCount = responses.filter(
          (entry) => entry?.status === 'completed',
        ).length;
        const failedEntries = responses.filter(
          (entry) => entry?.status === 'failed',
        );
        const pendingCount = responses.filter(
          (entry) =>
            entry?.status === 'pending' || entry?.status === 'in_progress',
        ).length;

        if (failedEntries.length > 0) {
          this.setBulkUpdateStatus(
            'error',
            failedEntries[0]?.message || 'One or more bulk update jobs failed.',
            false,
          );
          return;
        }

        if (completedCount === taskIds.length) {
          this.setBulkUpdateStatus('success', 'Updated', false);
          await this.loadData();
          this.hideBulkUpdateStatus(2000);
          return;
        }

        this.setBulkUpdateStatus('info', 'Updating...', true);
        this.bulkStatusPollHandle = setTimeout(() => {
          this.pollBulkUpdateTasks(taskIds);
        }, 2000);
      } catch (error) {
        console.error('Failed polling bulk update status', error);
        this.setBulkUpdateStatus(
          'error',
          'Could not check bulk update progress. Please refresh in a moment.',
          false,
        );
      }
    },
    init() {
      // Reset transient edit state each time this section initializes.
      this.changedCells = {};
      this.allRateSetOptions = [];
      const container = document.getElementById('calendarSettingsContainer');
      this.bookingMode = container?.dataset?.bookingMode || '';
      const savedFieldsRaw = container?.dataset?.selectorPricingFields || '';
      const savedRateSetIdsRaw =
        container?.dataset?.selectorPricingRateSetIds || '';
      const savedDaysRaw = container?.dataset?.selectorPricingDays || '';
      const savedNavigationStepDaysRaw =
        container?.dataset?.selectorPricingNavigationStepDays || '';
      const savedFields = savedFieldsRaw
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
      const savedRateSetIds = savedRateSetIdsRaw
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
      if (savedFields.length > 0) {
        this.selectedFields = Array.from(new Set(savedFields));
      }
      this.selectedRateSetIds = Array.from(new Set(savedRateSetIds));
      const parsedDays = Number.parseInt(savedDaysRaw, 10);
      if ([7, 14, 21].includes(parsedDays)) {
        this.days = parsedDays;
      }
      const parsedNavigationStepDays = Number.parseInt(
        savedNavigationStepDaysRaw,
        10,
      );
      if ([3, 7, 14, 30].includes(parsedNavigationStepDays)) {
        this.navigationStepDays = parsedNavigationStepDays;
      } else if (![3, 7, 14, 30].includes(this.navigationStepDays)) {
        this.navigationStepDays = this.days === 14 ? 7 : this.days;
      }
      this.pendingSelectedFields = [...this.selectedFields];
      this.pendingSelectedRateSetIds = [...this.selectedRateSetIds];
      this.applyInstantBookFieldRestrictions();
      this.initializeSettingsContext();
      if (!this.currentDate) {
        const selectorDate = container?.dataset?.selectorLastDate || '';
        const selectorDateIsValid = /^\d{4}-\d{2}-\d{2}$/.test(selectorDate);
        this.currentDate = selectorDateIsValid
          ? selectorDate
          : new Date().toISOString().slice(0, 10);
      }
      if (!this._pricingTableResizeHandlerRegistered) {
        window.addEventListener('resize', () =>
          this.updateTableScrollIndicators(),
        );
        this._pricingTableResizeHandlerRegistered = true;
      }
      if (!this._beforeUnloadHandlerRegistered) {
        this._beforeUnloadHandler = (event) => this.handleBeforeUnload(event);
        window.addEventListener('beforeunload', this._beforeUnloadHandler);
        this._beforeUnloadHandlerRegistered = true;
      }
      this.loadData();
    },
    handleBeforeUnload(event) {
      if (!this.hasChanges) {
        return undefined;
      }
      event.preventDefault();
      // Required for cross-browser beforeunload prompt support.
      event.returnValue = '';
      return '';
    },
    teardownTableScrollListeners() {
      if (this.tableScrollerElement && this._pricingTableWheelHandler) {
        this.tableScrollerElement.removeEventListener(
          'wheel',
          this._pricingTableWheelHandler,
        );
      }
      if (
        this.flexibleHeaderStickyElement &&
        this._pricingTableWheelHandler &&
        this._flexibleHeaderWheelListenerAttached
      ) {
        this.flexibleHeaderStickyElement.removeEventListener(
          'wheel',
          this._pricingTableWheelHandler,
        );
      }
      this._pricingTableWheelHandler = null;
      this._flexibleHeaderWheelListenerAttached = false;
      this.tableScrollerElement = null;
      this.flexibleHeaderStickyElement = null;
      this.flexibleHeaderTableElement = null;
    },
    attachFlexibleHeaderWheelListener() {
      if (
        !this.flexibleHeaderStickyElement ||
        !this._pricingTableWheelHandler ||
        this._flexibleHeaderWheelListenerAttached
      ) {
        return;
      }
      this.flexibleHeaderStickyElement.addEventListener(
        'wheel',
        this._pricingTableWheelHandler,
        { passive: false },
      );
      this._flexibleHeaderWheelListenerAttached = true;
    },
    initializeFlexibleHeaderScroll(element) {
      this.flexibleHeaderStickyElement = element || null;
      this.flexibleHeaderTableElement =
        element?.querySelector('.pricing-flexible-header-table--dates') || null;
      if (this.tableScrollerElement) {
        this.syncFlexibleHeaderScrollLeft(this.tableScrollerElement.scrollLeft);
      }
      this.attachFlexibleHeaderWheelListener();
    },
    syncFlexibleHeaderScrollLeft(scrollLeft) {
      if (!this.flexibleHeaderTableElement) {
        return;
      }
      const transform = scrollLeft ? `translateX(-${scrollLeft}px)` : '';
      if (this.flexibleHeaderTableElement.style.transform === transform) {
        return;
      }
      this.flexibleHeaderTableElement.style.transform = transform;
    },
    syncFlexibleBodyScroll(event) {
      const scrollLeft = event?.target?.scrollLeft || 0;
      this.syncFlexibleHeaderScrollLeft(scrollLeft);
      this.updateTableScrollIndicators();
    },
    initializeTableScrollIndicators(element) {
      if (!element) {
        return;
      }
      if (this.tableScrollerElement === element) {
        this.updateTableScrollIndicators();
        return;
      }
      if (this.tableScrollerElement && this.tableScrollerElement !== element) {
        this.teardownTableScrollListeners();
      }
      this.tableScrollerElement = element;
      if (!this._pricingTableWheelHandler) {
        this._pricingTableWheelHandler = (event) =>
          this.handlePricingTableWheel(event);
      }
      element.addEventListener('wheel', this._pricingTableWheelHandler, {
        passive: false,
      });
      this.attachFlexibleHeaderWheelListener();
      this.updateTableScrollIndicators();
      this.updatePinnedDateHeader();
    },
    updateTableScrollIndicators() {
      const element = this.tableScrollerElement;
      if (!element) {
        this.showTableScrollHintLeft = false;
        this.showTableScrollHintRight = false;
        return;
      }
      if (element.classList.contains('pricing-scroll-container--flexible')) {
        this.syncFlexibleHeaderScrollLeft(element.scrollLeft);
      }
      const canScroll = element.scrollWidth - element.clientWidth > 8;
      if (!canScroll) {
        this.showTableScrollHintLeft = false;
        this.showTableScrollHintRight = false;
        this.updatePinnedDateHeader();
        return;
      }
      this.showTableScrollHintLeft = element.scrollLeft > 8;
      this.showTableScrollHintRight =
        element.scrollLeft + element.clientWidth < element.scrollWidth - 8;
      this.updatePinnedDateHeader();
    },
    updatePinnedDateHeader() {
      const element = this.tableScrollerElement;
      if (
        !element ||
        element.classList.contains('pricing-scroll-container--flexible')
      ) {
        return;
      }
      const tableHead = element.querySelector('table thead');
      if (!tableHead) {
        return;
      }
      const scrollTop = element.scrollTop || 0;
      tableHead.style.transform = `translateY(${scrollTop}px)`;
      tableHead.style.position = 'relative';
      tableHead.style.zIndex = '20';
    },
    preventWheelOnFocusedNumberInput(event) {
      const target = event.target;
      if (
        !target ||
        !target.matches('input[type="number"]') ||
        target !== document.activeElement
      ) {
        return;
      }
      const deltaX = Math.abs(event.deltaX);
      const deltaY = Math.abs(event.deltaY);
      if (deltaY > deltaX) {
        // Prevent accidental value changes while still allowing the page (or
        // scroll container) to continue scrolling on wheel.
        target.blur();
      }
    },
    handlePricingTableWheel(event) {
      const element = this.tableScrollerElement;
      const inBodyScroller = element?.contains(event.target);
      const inHeaderSticky = this.flexibleHeaderStickyElement?.contains(
        event.target,
      );
      if (!element || (!inBodyScroller && !inHeaderSticky)) {
        return;
      }
      const canScrollHorizontal = element.scrollWidth - element.clientWidth > 8;
      if (!canScrollHorizontal) {
        return;
      }
      let deltaX = event.deltaX;
      const deltaY = event.deltaY;
      if (event.shiftKey && Math.abs(deltaY) > Math.abs(deltaX)) {
        deltaX = deltaY;
      }
      if (Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }
      const maxScrollLeft = element.scrollWidth - element.clientWidth;
      const nextScrollLeft = element.scrollLeft + deltaX;
      const atLeft = element.scrollLeft <= 0;
      const atRight = element.scrollLeft >= maxScrollLeft - 1;
      const scrollingLeft = deltaX < 0;
      const scrollingRight = deltaX > 0;
      if ((scrollingLeft && atLeft) || (scrollingRight && atRight)) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      const clampedScrollLeft = Math.max(
        0,
        Math.min(maxScrollLeft, nextScrollLeft),
      );
      element.scrollLeft = clampedScrollLeft;
      this.syncFlexibleHeaderScrollLeft(clampedScrollLeft);
    },
    isInstantBookEnabledForCalendar() {
      return this.bookingMode !== 'RO';
    },
    isInstantBookFieldAvailable(field) {
      return true;
    },
    applyInstantBookFieldRestrictions() {
      if (this.isInstantBookEnabledForCalendar()) {
        return;
      }
      this.bulkForm.instant_book_enabled_active = false;
      this.bulkForm.instant_book_enabled = [];
    },
    labelForField(field) {
      const labels = {
        price: 'Price',
        availability: 'Availability',
        min_length: 'Min Length',
        max_length: 'Max Length',
        closed_to_arrival: 'Closed To Arrival',
        closed_to_departure: 'Closed To Departure',
        instant_book_enabled: 'Instant Book',
        is_blocked: 'Blocked',
      };
      return labels[field] || field;
    },
    getVisibleFieldFilterOptions() {
      const available = new Set(this.fieldOptions || []);
      return FIELD_DISPLAY_ORDER.filter(
        (field) => field === 'instant_book_enabled' || available.has(field),
      );
    },
    getFieldsForDataLoad() {
      // Fixed intervals should always receive their core fields regardless
      // of flexible-table visibility filters.
      return Array.from(
        new Set([
          ...(this.selectedFields || []),
          'price',
          'instant_book_enabled',
          'is_blocked',
        ]),
      );
    },
    getRateSetRows(rateSet) {
      if (
        !rateSet ||
        !Array.isArray(rateSet.rows) ||
        rateSet.rows.length === 0
      ) {
        return [
          {
            label: 'Base Price',
            people: null,
            cells: [],
          },
        ];
      }
      return rateSet.rows;
    },
    getPrimaryRateSetRow(rateSet) {
      const rows = this.getRateSetRows(rateSet);
      return rows[0];
    },
    getPriceRowLabel(priceRow) {
      if (
        !priceRow ||
        priceRow.people === null ||
        typeof priceRow.people === 'undefined'
      ) {
        return 'Base';
      }
      const people = Number(priceRow.people);
      if (Number.isNaN(people)) {
        return 'Base';
      }
      return people === 1 ? '1 person' : `${people} people`;
    },
    isDerivedPackageRateSet(rateSet) {
      const capabilities = rateSet?.capabilities || {};
      const capabilityMode = capabilities.packagePricingMode;
      const legacyMode = rateSet?.package_pricing_mode;
      return (
        capabilityMode === 'COMPONENT_SUM' || legacyMode === 'COMPONENT_SUM'
      );
    },
    buildFlexibleDisplayRowsFromGroups() {
      const rows = [];
      (this.flexibleGroups || []).forEach((group) => {
        if (!Array.isArray(group.ratesets) || group.ratesets.length === 0) {
          return;
        }
        rows.push({
          row_type: 'group',
          key: `group-${group.key}`,
          group_key: group.key,
          group_label: group.label,
        });
        group.ratesets.forEach((rateSet) => {
          rows.push({
            row_type: 'rateset',
            key: `rateset-${group.key}-${rateSet.id}`,
            group_key: group.key,
            rate_set: rateSet,
          });
        });
      });
      return rows;
    },
    withDerivedPriceBanners(rows) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const outputRows = [];
      const shouldIncludeDerivedPriceBanners = this.isFieldVisible('price');
      sourceRows.forEach((row) => {
        if (row?.row_type !== 'rateset') {
          outputRows.push(row);
          return;
        }

        const baseKey = row.key || `rateset-${row?.rate_set?.id || 'unknown'}`;
        outputRows.push({
          ...row,
          row_type: 'rateset_header',
          key: `${baseKey}-header`,
        });
        outputRows.push({
          ...row,
          row_type: 'rateset_fields',
          key: `${baseKey}-fields`,
        });

        if (
          shouldIncludeDerivedPriceBanners &&
          this.isDerivedPackageRateSet(row?.rate_set)
        ) {
          outputRows.push({
            row_type: 'derived_price_banner',
            key: `${baseKey}-derived-banner`,
            group_key: row.group_key,
            rate_set: row.rate_set,
          });
        }
      });
      return outputRows;
    },
    buildFixedDisplayRowsFromGroups() {
      const rows = [];
      (this.fixedGroups || []).forEach((group) => {
        if (!Array.isArray(group.ratesets) || group.ratesets.length === 0) {
          return;
        }
        rows.push({
          row_type: 'group',
          key: `fixed-group-${group.key}`,
          group_key: group.key,
          group_label: group.label,
        });
        group.ratesets.forEach((rateSet) => {
          rows.push({
            row_type: 'rateset',
            key: `fixed-rateset-${group.key}-${rateSet.id}`,
            group_key: group.key,
            rate_set: rateSet,
          });
        });
      });
      return rows;
    },
    withFixedTableRows(rows) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const outputRows = [];
      sourceRows.forEach((row) => {
        if (row?.row_type !== 'rateset') {
          outputRows.push(row);
          return;
        }
        const baseKey =
          row.key || `fixed-rateset-${row?.rate_set?.id || 'unknown'}`;
        outputRows.push({
          ...row,
          row_type: 'fixed_rateset_header',
          key: `${baseKey}-header`,
        });
        outputRows.push({
          ...row,
          row_type: 'fixed_rateset_table',
          key: `${baseKey}-table`,
        });
      });
      return outputRows;
    },
    normalizeFixedRateSet(rateSet) {
      const intervalRows = Array.isArray(rateSet?.interval_rows)
        ? rateSet.interval_rows
        : [];
      return {
        ...rateSet,
        interval_rows: intervalRows,
        fixed_price_rows: this.buildFixedPriceRows(rateSet, intervalRows),
        fixed_intervals: this.buildFixedIntervals(rateSet, intervalRows),
      };
    },
    buildFixedPriceRows(rateSet, intervalRows) {
      if (this.isDerivedPackageRateSet(rateSet)) {
        return [{ label: 'Price', people: null, rates: [] }];
      }
      const byPeople = new Map();
      intervalRows.forEach((row) => {
        const peopleKey =
          row.people === null || typeof row.people === 'undefined'
            ? 'base'
            : String(row.people);
        if (!byPeople.has(peopleKey)) {
          byPeople.set(peopleKey, {
            label: this.getPriceRowLabel({ people: row.people }),
            people: row.people ?? null,
            rates: [],
          });
        }
        byPeople.get(peopleKey).rates.push({
          id: row.rate_id,
          date: row.start_date,
          end_date: row.end_date,
          price: row.price,
          instant_book_enabled: row.instant_book_enabled,
          is_blocked: row.is_blocked,
        });
      });
      if (byPeople.size === 0) {
        if (rateSet?.capabilities?.supportsOccupancyPricing) {
          const peoplePerUnit = rateSet.people_per_unit || 0;
          if (peoplePerUnit > 0) {
            return Array.from({ length: peoplePerUnit }, (_value, index) => ({
              label: this.getPriceRowLabel({ people: index + 1 }),
              people: index + 1,
              rates: [],
            }));
          }
        }
        return [{ label: 'Base Price', people: null, rates: [] }];
      }
      return Array.from(byPeople.values()).sort((left, right) => {
        const leftPeople = left.people ?? 0;
        const rightPeople = right.people ?? 0;
        return leftPeople - rightPeople;
      });
    },
    buildFixedIntervals(rateSet, intervalRows) {
      const intervalsMap = new Map();
      intervalRows.forEach((row) => {
        const key = `${row.start_date}|${row.end_date || ''}`;
        if (!intervalsMap.has(key)) {
          intervalsMap.set(key, {
            start_date: row.start_date,
            end_date: row.end_date,
            rates: [],
          });
        }
        intervalsMap.get(key).rates.push(row);
      });
      return Array.from(intervalsMap.values())
        .map((interval) => ({
          start_date: interval.start_date,
          end_date: interval.end_date,
          is_bookable: this.isFixedIntervalBookable(rateSet, interval.rates),
        }))
        .sort((left, right) => {
          if (left.start_date === right.start_date) {
            return (left.end_date || '').localeCompare(right.end_date || '');
          }
          return left.start_date.localeCompare(right.start_date);
        });
    },
    isFixedIntervalBookable(rateSet, ratesForInterval) {
      if (!Array.isArray(ratesForInterval) || ratesForInterval.length === 0) {
        return false;
      }
      if (this.isDerivedPackageRateSet(rateSet)) {
        return ratesForInterval.some((rate) => !rate.is_blocked);
      }
      return ratesForInterval.every((rate) => {
        const parsedPrice = Number.parseFloat(rate.price);
        const hasPrice = Number.isFinite(parsedPrice) && parsedPrice > 0;
        return hasPrice && rate.is_blocked !== true;
      });
    },
    getFixedPriceRows(rateSet) {
      if (!rateSet || !Array.isArray(rateSet.fixed_price_rows)) {
        return [{ label: 'Base Price', people: null, rates: [] }];
      }
      return rateSet.fixed_price_rows;
    },
    getFixedIntervals(rateSet) {
      if (!rateSet || !Array.isArray(rateSet.fixed_intervals)) {
        return [];
      }
      return rateSet.fixed_intervals;
    },
    getFixedRateForInterval(rateSet, priceRow, startDate, endDate) {
      if (!priceRow || !Array.isArray(priceRow.rates)) {
        return null;
      }
      const normalizeDate = (value) =>
        value === null || typeof value === 'undefined' ? '' : String(value);
      const normalizeEndDate = (value) =>
        value === '' || value === null || typeof value === 'undefined'
          ? null
          : String(value);
      const normalizePeople = (value) => {
        if (value === null || typeof value === 'undefined' || value === '') {
          return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const targetStartDate = normalizeDate(startDate);
      const targetEndDate = normalizeEndDate(endDate);
      const targetPeople = normalizePeople(priceRow.people);

      const directMatch =
        priceRow.rates.find((rate) => {
          const rateStartDate = normalizeDate(rate?.date ?? rate?.start_date);
          const rateEndDate = normalizeEndDate(rate?.end_date ?? rate?.endDate);
          if (
            rateStartDate !== targetStartDate ||
            rateEndDate !== targetEndDate
          ) {
            return false;
          }
          if (targetPeople === null) {
            return true;
          }
          return normalizePeople(rate?.people) === targetPeople;
        }) || null;
      if (directMatch) {
        return directMatch;
      }

      const fallbackRow = (rateSet?.interval_rows || []).find((row) => {
        const rowStartDate = normalizeDate(row?.start_date ?? row?.date);
        const rowEndDate = normalizeEndDate(row?.end_date ?? row?.endDate);
        if (rowStartDate !== targetStartDate || rowEndDate !== targetEndDate) {
          return false;
        }
        if (targetPeople === null) {
          return true;
        }
        return normalizePeople(row?.people) === targetPeople;
      });
      if (!fallbackRow) {
        return null;
      }
      return {
        id: fallbackRow.rate_id ?? fallbackRow.id ?? null,
        date: fallbackRow.start_date ?? fallbackRow.date ?? null,
        end_date: fallbackRow.end_date ?? null,
        price: fallbackRow.price,
        instant_book_enabled: fallbackRow.instant_book_enabled,
        is_blocked: fallbackRow.is_blocked,
        people: fallbackRow.people ?? null,
      };
    },
    getFixedIntervalPrimaryRate(rateSet, startDate, endDate) {
      const priceRows = this.getFixedPriceRows(rateSet);
      for (const priceRow of priceRows) {
        const rate = this.getFixedRateForInterval(
          rateSet,
          priceRow,
          startDate,
          endDate,
        );
        if (rate) {
          return rate;
        }
      }
      const normalizeEndDate = (value) =>
        value === '' || typeof value === 'undefined' ? null : value;
      const targetEndDate = normalizeEndDate(endDate);
      const fallbackRate = (rateSet?.interval_rows || []).find((row) => {
        if (!row || row.start_date !== startDate) {
          return false;
        }
        return normalizeEndDate(row.end_date) === targetEndDate;
      });
      if (fallbackRate) {
        return fallbackRate;
      }
      return null;
    },
    getFixedIntervalPriceSummary(rateSet, startDate, endDate) {
      if (this.isDerivedPackageRateSet(rateSet)) {
        return 'From components';
      }
      const priceRows = this.getFixedPriceRows(rateSet);
      const numericPrices = [];
      priceRows.forEach((priceRow) => {
        const rate = this.getFixedRateForInterval(
          rateSet,
          priceRow,
          startDate,
          endDate,
        );
        if (!rate) {
          return;
        }
        const parsedPrice = Number.parseFloat(rate.price);
        if (Number.isFinite(parsedPrice)) {
          numericPrices.push(parsedPrice);
        }
      });
      if (numericPrices.length === 0) {
        return 'No rate data';
      }
      const uniqueSortedPrices = Array.from(new Set(numericPrices)).sort(
        (left, right) => left - right,
      );
      const formatPrice = (value) =>
        Number(value).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      if (uniqueSortedPrices.length === 1) {
        return formatPrice(uniqueSortedPrices[0]);
      }
      return `${formatPrice(uniqueSortedPrices[0])} - ${formatPrice(
        uniqueSortedPrices[uniqueSortedPrices.length - 1],
      )}`;
    },
    formatFixedDateRange(rateSet, startDate, endDate) {
      const formatDate = (dateString) => {
        if (!dateString) {
          return '-';
        }
        const dateObj = new Date(`${dateString}T00:00:00`);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const monthAbbreviations = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        const month = monthAbbreviations[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        return `${day} ${month} ${year}`;
      };
      let displayEndDate = endDate;
      if (this.usesCheckoutDisplayEndDate(rateSet) && endDate) {
        const adjustedEndDate = new Date(`${endDate}T00:00:00`);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
        displayEndDate = formatDateISOCalendar(adjustedEndDate);
      }
      if (!endDate) {
        return formatDate(startDate);
      }
      return `${formatDate(startDate)} - ${formatDate(displayEndDate)}`;
    },
    usesCheckoutDisplayEndDate(rateSet) {
      if (!rateSet) return false;
      if (this.isAccommodationRateSet(rateSet)) return true;
      // Package fixed intervals should also display checkout-style end dates.
      if (rateSet.is_package === true) return true;
      const groupKey = String(rateSet.group_key || '')
        .trim()
        .toLowerCase();
      if (groupKey === 'packages') return true;
      const typeKey = String(rateSet.type_key || '')
        .trim()
        .toLowerCase();
      if (typeKey === 'packages') return true;
      return false;
    },
    isFixedIntervalAvailable(rateSet, interval) {
      if (!interval) {
        return false;
      }
      if (Object.prototype.hasOwnProperty.call(interval, 'is_bookable')) {
        return !!interval.is_bookable;
      }
      const ratesForInterval = (rateSet?.interval_rows || []).filter(
        (row) =>
          row.start_date === interval.start_date &&
          row.end_date === interval.end_date,
      );
      return this.isFixedIntervalBookable(rateSet, ratesForInterval);
    },
    getNewFixedInterval(rateSetId) {
      const key = String(rateSetId);
      if (!this.newFixedIntervalByRateSetId[key]) {
        this.newFixedIntervalByRateSetId[key] = {
          dateRange: '',
          price: '',
          prices: [],
        };
      }
      return this.newFixedIntervalByRateSetId[key];
    },
    usesOccupancyPricing(rateSet) {
      return this.getFixedAddOccupancyPeopleCounts(rateSet).length > 0;
    },
    getFixedAddOccupancyPeopleCounts(rateSet) {
      if (!rateSet || this.isDerivedPackageRateSet(rateSet)) {
        return [];
      }
      const occupancyPeopleValues = Array.from(
        new Set(
          this.getFixedPriceRows(rateSet)
            .map((row) => Number(row?.people))
            .filter((value) => Number.isFinite(value) && value > 0)
            .map((value) => Math.floor(value)),
        ),
      ).sort((left, right) => left - right);

      if (occupancyPeopleValues.length > 0) {
        // A single "1 person" tier should behave as base pricing in the add row.
        if (
          occupancyPeopleValues.length === 1 &&
          occupancyPeopleValues[0] === 1
        ) {
          return [];
        }
        return occupancyPeopleValues;
      }

      // If fixed intervals already exist but no occupancy tiers were found, treat as base.
      if (this.getFixedIntervals(rateSet).length > 0) {
        return [];
      }

      // For brand-new rate sets with no fixed rows yet, fall back to metadata.
      const peopleFromMeta = Number(
        rateSet?.people_per_unit ?? rateSet?.capabilities?.peoplePerUnit ?? 0,
      );
      const hasOccupancyFlag =
        rateSet?.people_influence_price === true ||
        rateSet?.peopleInfluencePrice === true ||
        rateSet?.capabilities?.supportsOccupancyPricing === true;
      if (
        hasOccupancyFlag &&
        Number.isFinite(peopleFromMeta) &&
        Math.floor(peopleFromMeta) > 1
      ) {
        return Array.from(
          { length: Math.floor(peopleFromMeta) },
          (_value, index) => index + 1,
        );
      }
      return [];
    },
    getOccupancyPeopleCount(rateSet) {
      const counts = this.getFixedAddOccupancyPeopleCounts(rateSet);
      if (counts.length > 0) {
        return counts.length;
      }
      return 1;
    },
    ensureNewFixedIntervalPrices(rateSet) {
      const entry = this.getNewFixedInterval(rateSet.id);
      if (!this.usesOccupancyPricing(rateSet)) {
        entry.prices = [];
        return entry;
      }
      const requiredLength = this.getOccupancyPeopleCount(rateSet);
      const existing = Array.isArray(entry.prices) ? entry.prices : [];
      entry.prices = Array.from(
        { length: requiredLength },
        (_value, index) => existing[index] ?? '',
      );
      return entry;
    },
    initializeNewFixedIntervalPicker(rateSet, element) {
      if (!element || typeof flatpickr === 'undefined' || !rateSet) {
        return;
      }
      const rateSetId = String(rateSet.id);
      const existing = this._fixedIntervalPickerInstances[rateSetId];
      if (existing) {
        existing.destroy();
      }
      const entry = this.ensureNewFixedIntervalPrices(rateSet);
      const today = new Date();
      const maxDate = new Date(today);
      maxDate.setFullYear(today.getFullYear() + 2);
      this._fixedIntervalPickerInstances[rateSetId] = flatpickr(element, {
        mode: 'range',
        dateFormat: 'd/m/Y',
        showMonths: 2,
        maxDate,
        onChange: (_selectedDates, dateStr) => {
          entry.dateRange = dateStr || '';
        },
      });
    },
    trackFixedFieldChange(rateSet, rate, field, rawValue) {
      if (!rateSet || !rate?.id) {
        return;
      }
      const boolFields = ['instant_book_enabled', 'is_blocked'];
      const numericFields = ['price'];
      let value = rawValue;
      if (boolFields.includes(field)) {
        value = !!rawValue;
      } else if (numericFields.includes(field)) {
        value = parseNumeric(rawValue);
        if (value === null) {
          if (field === 'price') {
            value = 0;
          } else {
            return;
          }
        } else if (field === 'price') {
          value = clampPriceValue(value);
        }
      }
      rate[field] = value;
      const key = `${rateSet.id}|fixed|${rate.id}`;
      if (!this.changedCells[key]) {
        this.changedCells[key] = {
          rateSetId: rateSet.id,
          rateType: rateSet.rate_type,
          people: rate.people ?? null,
          date: rate.date,
          rateId: rate.id,
          fields: {},
        };
      }
      this.changedCells[key].fields[field] = value;
    },
    trackFixedIntervalFieldChange(rateSet, startDate, endDate, field, value) {
      const priceRows = this.getFixedPriceRows(rateSet);
      priceRows.forEach((priceRow) => {
        const rate = this.getFixedRateForInterval(
          rateSet,
          priceRow,
          startDate,
          endDate,
        );
        if (rate) {
          this.trackFixedFieldChange(rateSet, rate, field, value);
        }
      });
    },
    hasFixedRateFieldChange(rateSet, rateId, field) {
      if (!rateSet || !rateId) {
        return false;
      }
      const key = `${rateSet.id}|fixed|${rateId}`;
      return !!this.changedCells[key]?.fields?.hasOwnProperty(field);
    },
    hasFixedIntervalFieldChange(rateSet, startDate, endDate, field) {
      const priceRows = this.getFixedPriceRows(rateSet);
      return priceRows.some((priceRow) => {
        const rate = this.getFixedRateForInterval(
          rateSet,
          priceRow,
          startDate,
          endDate,
        );
        return rate && this.hasFixedRateFieldChange(rateSet, rate.id, field);
      });
    },
    getFixedIntervalRateIds(rateSet, startDate, endDate) {
      return (rateSet?.interval_rows || [])
        .filter(
          (row) => row.start_date === startDate && row.end_date === endDate,
        )
        .map((row) => row.rate_id)
        .filter(Boolean);
    },
    async submitNewFixedInterval(rateSet) {
      if (!rateSet) {
        return;
      }
      const entry = this.ensureNewFixedIntervalPrices(rateSet);
      if (!entry.dateRange) {
        alert('Please select a date range');
        return;
      }
      let resolvedPrices = Array.isArray(entry.prices) ? [...entry.prices] : [];
      let resolvedSinglePrice = parseNumeric(entry.price);
      const isOccupancyPricing = this.usesOccupancyPricing(rateSet);
      if (isOccupancyPricing) {
        const requiredLength = this.getOccupancyPeopleCount(rateSet);
        resolvedPrices = Array.from(
          { length: requiredLength },
          (_value, index) => resolvedPrices[index] ?? '',
        );
        const hasAnyTierPrice = resolvedPrices.some(
          (price) => price !== null && price !== '',
        );
        if (!hasAnyTierPrice && resolvedSinglePrice !== null) {
          resolvedPrices = Array.from({ length: requiredLength }, () =>
            String(clampPriceValue(resolvedSinglePrice)),
          );
          entry.prices = [...resolvedPrices];
        }
        if (resolvedPrices.some((price) => price === null || price === '')) {
          alert('Please fill in all price fields');
          return;
        }
      } else if (!entry.price) {
        alert('Please enter a price');
        return;
      }
      const dateParts = entry.dateRange.split(' to ');
      if (dateParts.length !== 2) {
        alert('Please select a complete date range');
        return;
      }
      const toIsoDate = (dateText) => {
        const [day, month, year] = dateText.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      };
      const payload = {
        start_date: toIsoDate(dateParts[0]),
        end_date: toIsoDate(dateParts[1]),
        rateSetId: rateSet.id,
        people_influence_price: isOccupancyPricing,
      };
      const normalizeStoredEndDate = (value) =>
        value === '' || typeof value === 'undefined' ? null : value;
      let storedEndDateForComparison = payload.end_date;
      if (
        this.usesCheckoutDisplayEndDate(rateSet) &&
        storedEndDateForComparison
      ) {
        const adjusted = new Date(`${storedEndDateForComparison}T00:00:00`);
        adjusted.setDate(adjusted.getDate() - 1);
        storedEndDateForComparison = formatDateISOCalendar(adjusted);
      }
      const duplicateExists = this.getFixedIntervals(rateSet).some(
        (interval) =>
          interval.start_date === payload.start_date &&
          normalizeStoredEndDate(interval.end_date) ===
            normalizeStoredEndDate(storedEndDateForComparison),
      );
      if (duplicateExists) {
        alert('These fixed dates already exist.');
        return;
      }
      if (isOccupancyPricing) {
        payload.prices = resolvedPrices.map((price) =>
          clampPriceValue(Number.parseFloat(price)),
        );
      } else {
        payload.price = clampPriceValue(Number.parseFloat(entry.price));
      }
      this.loading = true;
      this.setBulkUpdateStatus('info', 'Updating...', true);
      try {
        const response = await fetch('/rates/add-fixed-interval/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to add fixed interval');
        }
        entry.dateRange = '';
        entry.price = '';
        entry.prices = Array.from(
          {
            length: isOccupancyPricing
              ? this.getOccupancyPeopleCount(rateSet)
              : 0,
          },
          () => '',
        );
        await this.loadData();
        this.setBulkUpdateStatus('success', 'Updated', false);
        this.hideBulkUpdateStatus(2000);
      } catch (error) {
        console.error('Failed to add fixed interval', error);
        this.setBulkUpdateStatus(
          'error',
          error.message || 'Failed to add fixed interval.',
          false,
        );
      } finally {
        this.loading = false;
      }
    },
    async deleteFixedInterval(rateSet, interval) {
      if (!rateSet || !interval) {
        return;
      }
      if (
        !window.confirm('Are you sure you want to delete this fixed interval?')
      ) {
        return;
      }
      const rateIds = this.getFixedIntervalRateIds(
        rateSet,
        interval.start_date,
        interval.end_date,
      );
      if (!rateIds.length) {
        return;
      }
      this.loading = true;
      try {
        const response = await fetch('/rates/delete-fixed-interval/', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({ rateIds }),
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to delete fixed interval');
        }
        await this.loadData();
      } catch (error) {
        console.error('Failed to delete fixed interval', error);
        alert(error.message || 'Failed to delete fixed interval.');
      } finally {
        this.loading = false;
      }
    },
    typeLabelForRateSet(rateSet) {
      if (!rateSet) return 'Other';
      if (rateSet.is_package) return 'Packages';
      if (rateSet.rate_type === 'Accommodation') return 'Accommodation';
      if (rateSet.rate_type === 'Tour') return 'Tour';
      return 'Other';
    },
    typeKeyForRateSet(rateSet) {
      if (!rateSet) return 'other';
      if (rateSet.is_package) return 'packages';
      if (rateSet.rate_type === 'Accommodation') return 'accommodation';
      if (rateSet.rate_type === 'Tour') return 'tour';
      return 'other';
    },
    isAccommodationRateSet(rateSet) {
      if (!rateSet) return false;
      const normalizeValue = (value) =>
        String(value || '')
          .trim()
          .toLowerCase();
      const isAccommodationLike = (value) =>
        normalizeValue(value).startsWith('accommodation');

      if (isAccommodationLike(rateSet.rate_type)) return true;
      if (isAccommodationLike(rateSet.capabilities?.rateType)) return true;
      if (isAccommodationLike(rateSet.group_key)) return true;
      if (isAccommodationLike(rateSet.type_key)) return true;
      if (rateSet.accommodation_type) return true;
      if (rateSet.accommodation_type_id) return true;
      return false;
    },
    isRateSetSelected(rateSetId) {
      return this.pendingSelectedRateSetIds.includes(String(rateSetId));
    },
    toggleRateSetSelection(rateSetId) {
      const id = String(rateSetId);
      if (this.pendingSelectedRateSetIds.includes(id)) {
        this.pendingSelectedRateSetIds = this.pendingSelectedRateSetIds.filter(
          (entry) => entry !== id,
        );
      } else {
        this.pendingSelectedRateSetIds = [
          ...this.pendingSelectedRateSetIds,
          id,
        ];
      }
    },
    clearRateSetSelection() {
      this.pendingSelectedRateSetIds = [];
    },
    selectAllFilteredRateSets() {
      const ids = this.getFilteredRateSetOptions().map((entry) =>
        String(entry.id),
      );
      this.pendingSelectedRateSetIds = Array.from(new Set(ids));
    },
    applyRateSetFilters() {
      this.selectedRateSetIds = Array.from(
        new Set(this.pendingSelectedRateSetIds || []),
      );
      this.persistPricingFilters();
      this.loadData();
    },
    getFilteredRateSetOptions() {
      let options = [...this.rateSetOptions];
      if (this.ratesetSearch.trim()) {
        const term = this.ratesetSearch.trim().toLowerCase();
        options = options.filter((entry) =>
          (entry.public_name || '').toLowerCase().includes(term),
        );
      }
      return options;
    },
    getGroupedRateSetOptions() {
      const groups = {
        accommodation: [],
        tour: [],
        packages: [],
      };
      this.getFilteredRateSetOptions().forEach((entry) => {
        const typeKey = this.typeKeyForRateSet(entry);
        if (groups[typeKey]) {
          groups[typeKey].push(entry);
        }
      });
      return groups;
    },
    getDateAvailability(rateSet, day) {
      if (
        rateSet &&
        rateSet.availability_by_date &&
        Object.prototype.hasOwnProperty.call(rateSet.availability_by_date, day)
      ) {
        return rateSet.availability_by_date[day];
      }
      if (!rateSet || !Array.isArray(rateSet.rows)) return null;
      const availabilityValues = rateSet.rows
        .map((row) => this.getOrCreateCell(row, day)?.availability)
        .filter((value) => value !== null && typeof value !== 'undefined');
      if (availabilityValues.length === 0) return null;
      return Math.max(...availabilityValues);
    },
    getDateAvailabilityClass(rateSet, day) {
      const computedBookable = this.getDateBookableFromRows(rateSet, day);
      return computedBookable ? 'date-available' : 'date-unavailable';
    },
    getDateBookableFromRows(rateSet, day) {
      if (
        rateSet?.capabilities?.isDerivedPrice &&
        rateSet?.bookable_by_date &&
        Object.prototype.hasOwnProperty.call(rateSet.bookable_by_date, day)
      ) {
        const rawBookable = rateSet.bookable_by_date[day];
        return (
          rawBookable === true ||
          rawBookable === 1 ||
          (typeof rawBookable === 'string' &&
            rawBookable.toLowerCase() === 'true')
        );
      }
      if (
        !rateSet ||
        !Array.isArray(rateSet.rows) ||
        rateSet.rows.length === 0
      ) {
        return false;
      }

      const rowBookables = rateSet.rows.map((row) => {
        const cell = this.getCell(row, day);
        if (!cell) {
          return false;
        }
        const parsedPrice = Number.parseFloat(cell?.price);
        const hasPrice = Number.isFinite(parsedPrice) && parsedPrice > 0;
        const notBlocked = cell?.is_blocked !== true;
        return hasPrice && notBlocked;
      });

      // For occupancy-based pricing, all visible price rows must be bookable.
      // For single-price ratesets this naturally resolves to the single row.
      return rowBookables.every(Boolean);
    },
    getDateAvailabilityBadgeClass(rateSet, day) {
      const availability = this.getDateAvailability(rateSet, day);
      const linkedUnitsCount =
        typeof rateSet?.linked_units_count === 'number'
          ? rateSet.linked_units_count
          : 0;

      if (availability === null || typeof availability === 'undefined') {
        const isBookable = this.getDateBookableFromRows(rateSet, day);
        return isBookable
          ? 'availability-badge-none'
          : 'availability-badge-unbookable';
      }
      if (linkedUnitsCount > 0 && availability >= linkedUnitsCount) {
        return 'availability-badge-full';
      }
      if (availability > 0) {
        return 'availability-badge-partial';
      }
      return 'availability-badge-none';
    },
    getDateAvailabilityBadgeValue(rateSet, day) {
      const availability = this.getDateAvailability(rateSet, day);
      if (availability === null || typeof availability === 'undefined') {
        return '-';
      }
      return availability;
    },
    getFixedIntervalAvailabilityClass(rateSet, interval) {
      if (!interval) {
        return 'date-unavailable';
      }
      return this.isFixedIntervalAvailable(rateSet, interval)
        ? 'date-available'
        : 'date-unavailable';
    },
    getOrCreateCell(row, date) {
      if (!row || !Array.isArray(row.cells)) {
        return {
          date,
          rate_id: null,
          people: row?.people ?? null,
          availability: null,
        };
      }
      const existing = row.cells.find((cell) => cell.date === date);
      if (existing) {
        return existing;
      }
      const placeholder = {
        date,
        rate_id: null,
        people: row.people ?? null,
        availability: null,
      };
      row.cells.push(placeholder);
      return placeholder;
    },
    getCell(row, date) {
      if (!row || !Array.isArray(row.cells)) {
        return null;
      }
      return row.cells.find((cell) => cell.date === date) || null;
    },
    isFieldVisible(field) {
      return this.selectedFields.includes(field);
    },
    isPendingFieldSelected(field) {
      return this.pendingSelectedFields.includes(field);
    },
    parseLocalDate(dateString) {
      if (!dateString) {
        return null;
      }
      const parts = dateString.split('-');
      if (parts.length !== 3) {
        return null;
      }
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return new Date(year, month, day);
    },
    isWeekend(dateString) {
      const dateObj = this.parseLocalDate(dateString);
      if (!dateObj) {
        return false;
      }
      const dayOfWeek = dateObj.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6;
    },
    formatDay(dateString) {
      const dateObj = this.parseLocalDate(dateString);
      if (!dateObj) {
        return '';
      }
      return dateObj.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    },
    formatWeekday(dateString) {
      const dateObj = this.parseLocalDate(dateString);
      if (!dateObj) {
        return '';
      }
      return dateObj.toLocaleDateString(undefined, {
        weekday: 'short',
      });
    },
    toggleField(field) {
      if (!this.isInstantBookFieldAvailable(field)) {
        return;
      }
      if (this.pendingSelectedFields.includes(field)) {
        this.pendingSelectedFields = this.pendingSelectedFields.filter(
          (entry) => entry !== field,
        );
      } else {
        this.pendingSelectedFields = [...this.pendingSelectedFields, field];
      }
      if (this.pendingSelectedFields.length === 0) {
        this.pendingSelectedFields = ['price'];
      }
    },
    applyFieldFilters() {
      const normalized = Array.from(
        new Set(this.pendingSelectedFields || []),
      ).filter((field) => this.isInstantBookFieldAvailable(field));
      this.selectedFields = normalized.length > 0 ? normalized : ['price'];
      this.pendingSelectedFields = [...this.selectedFields];
      this.persistPricingFilters();
      this.loadData();
    },
    applyAllFilters() {
      const normalizedFields = Array.from(
        new Set(this.pendingSelectedFields || []),
      ).filter((field) => this.isInstantBookFieldAvailable(field));
      this.selectedFields =
        normalizedFields.length > 0 ? normalizedFields : ['price'];
      this.pendingSelectedFields = [...this.selectedFields];

      const normalizedRateSetIds = Array.from(
        new Set(this.pendingSelectedRateSetIds || []),
      );
      this.selectedRateSetIds = normalizedRateSetIds;
      this.pendingSelectedRateSetIds = [...this.selectedRateSetIds];

      this.persistPricingFilters();
      this.loadData();
      this.areFiltersCollapsed = true;
    },
    async persistPricingFilters() {
      try {
        await fetch('/rates/global-pricing-conditions-preferences/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            selected_fields: this.selectedFields,
            selected_rate_set_ids: this.selectedRateSetIds,
            days: this.days,
            navigation_step_days: this.navigationStepDays,
          }),
        });
      } catch (error) {
        console.warn('Failed to persist global pricing filters', error);
      }
    },
    shiftDate(offsetDays) {
      const baseDate = new Date(this.currentDate);
      baseDate.setDate(baseDate.getDate() + offsetDays);
      this.currentDate = baseDate.toISOString().slice(0, 10);
      if (this.startDatePickerInstance) {
        this.startDatePickerInstance.setDate(this.currentDate, false, 'Y-m-d');
      }
      this.loadData();
    },
    onDaysChanged() {
      if (![7, 14, 21].includes(this.days)) {
        this.days = 14;
      }
      this.persistPricingFilters();
      this.loadData();
    },
    onNavigationStepChanged() {
      if (![3, 7, 14, 30].includes(this.navigationStepDays)) {
        this.navigationStepDays = 7;
      }
      this.persistPricingFilters();
    },
    initializeStartDatePicker(element) {
      if (!element || typeof flatpickr === 'undefined') {
        return;
      }

      if (this.startDatePickerInstance) {
        this.startDatePickerInstance.setDate(this.currentDate, false, 'Y-m-d');
        return;
      }

      const defaultDate =
        this.currentDate || new Date().toISOString().slice(0, 10);
      this.startDatePickerInstance = flatpickr(element, {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'M j, Y',
        defaultDate,
        onChange: (_selectedDates, dateStr) => {
          if (!dateStr || dateStr === this.currentDate) {
            return;
          }
          this.currentDate = dateStr;
          this.loadData();
        },
      });
    },
    formatLocalDateISO(dateObj) {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    clearBulkDateRange() {
      this.bulkForm.start_date = '';
      this.bulkForm.end_date = '';
      if (this.bulkDateRangePickerInstance) {
        this.bulkDateRangePickerInstance.clear();
      }
    },
    initializeBulkDateRangePicker(element) {
      if (!element || typeof flatpickr === 'undefined') {
        return;
      }

      if (this.bulkDateRangePickerInstance) {
        return;
      }

      this.bulkDateRangePickerInstance = flatpickr(element, {
        mode: 'range',
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'M j, Y',
        onOpen: (_selectedDates, _dateStr, instance) => {
          if (this.currentDate) {
            instance.jumpToDate(new Date(this.currentDate));
          }
        },
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            const [startDate, endDate] = selectedDates;
            this.bulkForm.start_date = this.formatLocalDateISO(startDate);
            this.bulkForm.end_date = this.formatLocalDateISO(endDate);
          } else {
            this.bulkForm.start_date = '';
            this.bulkForm.end_date = '';
          }
        },
      });
    },
    updateBulkFieldStatus(field) {
      if (field === 'price' && !this.bulkForm.price_active) {
        this.bulkForm.price = '';
        this.bulkForm.prices = [];
      } else if (field === 'min_length' && !this.bulkForm.min_length_active) {
        this.bulkForm.min_length = '';
      } else if (field === 'max_length' && !this.bulkForm.max_length_active) {
        this.bulkForm.max_length = '';
      } else if (
        field === 'closed_to_arrival' &&
        !this.bulkForm.closed_to_arrival_active
      ) {
        this.bulkForm.closed_to_arrival = [];
      } else if (
        field === 'closed_to_departure' &&
        !this.bulkForm.closed_to_departure_active
      ) {
        this.bulkForm.closed_to_departure = [];
      } else if (
        field === 'instant_book_enabled' &&
        !this.bulkForm.instant_book_enabled_active
      ) {
        this.bulkForm.instant_book_enabled = [];
      } else if (field === 'is_blocked' && !this.bulkForm.is_blocked_active) {
        this.bulkForm.is_blocked = false;
      }
    },
    toggleClosedToArrival(day, isChecked) {
      const index = this.bulkForm.closed_to_arrival.indexOf(day);
      if (isChecked && index === -1) {
        this.bulkForm.closed_to_arrival.push(day);
      } else if (!isChecked && index > -1) {
        this.bulkForm.closed_to_arrival.splice(index, 1);
      }
    },
    toggleClosedToDeparture(day, isChecked) {
      const index = this.bulkForm.closed_to_departure.indexOf(day);
      if (isChecked && index === -1) {
        this.bulkForm.closed_to_departure.push(day);
      } else if (!isChecked && index > -1) {
        this.bulkForm.closed_to_departure.splice(index, 1);
      }
    },
    toggleInstantBookEnabled(day, isChecked) {
      if (!this.isInstantBookEnabledForCalendar()) {
        return;
      }
      const index = this.bulkForm.instant_book_enabled.indexOf(day);
      if (isChecked && index === -1) {
        this.bulkForm.instant_book_enabled.push(day);
      } else if (!isChecked && index > -1) {
        this.bulkForm.instant_book_enabled.splice(index, 1);
      }
    },
    toggleBulkSelection(rateSetId) {
      if (this.bulkSelection.includes(rateSetId)) {
        this.bulkSelection = this.bulkSelection.filter(
          (entry) => entry !== rateSetId,
        );
      } else {
        this.bulkSelection = [...this.bulkSelection, rateSetId];
      }
    },
    getBulkSelectedRateSets() {
      const selectedIds = new Set(
        this.bulkSelection.map((entry) => String(entry)),
      );
      return (this.flexibleRatesets || []).filter((rateSet) =>
        selectedIds.has(String(rateSet.id)),
      );
    },
    isBulkSingleSelection() {
      return this.getBulkSelectedRateSets().length === 1;
    },
    getBulkSingleRateSet() {
      const selected = this.getBulkSelectedRateSets();
      return selected.length === 1 ? selected[0] : null;
    },
    getBulkPriceCompatibility() {
      const selectedRateSets = this.getBulkSelectedRateSets();
      if (!selectedRateSets.length) {
        return { mode: 'none', peoplePerUnit: null };
      }

      const isSupportedForDirectPrice = (rateSet) => {
        if (!rateSet) return false;
        const capabilities = rateSet.capabilities || {};
        const supportsPriceEditing =
          capabilities.supportsPriceEditing !== false;
        return supportsPriceEditing && !this.isDerivedPackageRateSet(rateSet);
      };

      if (
        !selectedRateSets.every((rateSet) => isSupportedForDirectPrice(rateSet))
      ) {
        return { mode: 'none', peoplePerUnit: null };
      }

      const occupancyRateSets = selectedRateSets.filter((rateSet) => {
        const capabilities = rateSet.capabilities || {};
        return capabilities.supportsOccupancyPricing === true;
      });

      if (occupancyRateSets.length === 0) {
        return { mode: 'base', peoplePerUnit: null };
      }

      if (occupancyRateSets.length !== selectedRateSets.length) {
        return { mode: 'none', peoplePerUnit: null };
      }

      const peoplePerUnitSet = new Set(
        occupancyRateSets.map(
          (rateSet) => Number(rateSet.people_per_unit) || 0,
        ),
      );
      if (peoplePerUnitSet.size !== 1) {
        return { mode: 'none', peoplePerUnit: null };
      }

      const [peoplePerUnit] = Array.from(peoplePerUnitSet);
      if (!peoplePerUnit || peoplePerUnit < 1) {
        return { mode: 'none', peoplePerUnit: null };
      }

      return { mode: 'occupancy', peoplePerUnit };
    },
    canShowBulkPriceField() {
      return this.getBulkPriceCompatibility().mode !== 'none';
    },
    isBulkOccupancyPriceMode() {
      return this.getBulkPriceCompatibility().mode === 'occupancy';
    },
    getBulkOccupancyPeopleCounts() {
      const compatibility = this.getBulkPriceCompatibility();
      if (compatibility.mode !== 'occupancy') {
        return [];
      }
      return Array.from(
        { length: compatibility.peoplePerUnit },
        (_value, index) => index + 1,
      );
    },
    showBulkPriceIncompatibleWarning() {
      const selectedRateSets = this.getBulkSelectedRateSets();
      return selectedRateSets.length > 1 && !this.canShowBulkPriceField();
    },
    isBulkFieldSupportedForSingle(fieldName) {
      const selectedRateSet = this.getBulkSingleRateSet();
      if (!selectedRateSet) return true;
      const capabilities = selectedRateSet.capabilities || {};
      if (fieldName === 'price') {
        return (
          capabilities.supportsPriceEditing !== false &&
          !this.isDerivedPackageRateSet(selectedRateSet)
        );
      }
      if (fieldName === 'min_length' || fieldName === 'max_length') {
        return capabilities.supportsMinMax !== false;
      }
      if (
        fieldName === 'closed_to_arrival' ||
        fieldName === 'closed_to_departure'
      ) {
        return capabilities.supportsClosedToArrivalDeparture !== false;
      }
      if (fieldName === 'instant_book_enabled') {
        return (
          this.isInstantBookEnabledForCalendar() &&
          capabilities.supportsInstantBook !== false
        );
      }
      if (fieldName === 'is_blocked') {
        return capabilities.supportsBlocked !== false;
      }
      return true;
    },
    async loadData() {
      const requestId = this.loadRequestId + 1;
      this.loadRequestId = requestId;
      this.loading = true;
      try {
        const params = new URLSearchParams({
          date: this.currentDate,
          days: String(this.days),
          fields: this.getFieldsForDataLoad().join(','),
          rate_set_ids: this.selectedRateSetIds.join(','),
        });
        const response = await fetch(
          `/rates/global-pricing-conditions-data/?${params.toString()}`,
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Failed to load pricing data');
        }
        if (requestId !== this.loadRequestId) {
          return;
        }
        this.dates = data.dates || [];
        if (data.booking_mode) {
          this.bookingMode = data.booking_mode;
        }
        const incomingFieldOptions = Array.isArray(data.field_options)
          ? data.field_options
          : [];
        this.fieldOptions = Array.from(
          new Set([...incomingFieldOptions, 'instant_book_enabled']),
        );
        this.applyInstantBookFieldRestrictions();
        const validFieldOptionSet = new Set(this.fieldOptions);
        this.selectedFields = (this.selectedFields || []).filter((field) =>
          validFieldOptionSet.has(field),
        );
        if (this.selectedFields.length === 0) {
          if (validFieldOptionSet.has('price')) {
            this.selectedFields = ['price'];
          } else if (this.fieldOptions.length > 0) {
            this.selectedFields = [this.fieldOptions[0]];
          }
        }
        this.pendingSelectedFields = [...this.selectedFields];
        const normalizeFlexibleRateSet = (rateSet) => {
          const rows = Array.isArray(rateSet.rows) ? rateSet.rows : [];
          if (rows.length > 0) {
            return {
              ...rateSet,
              rows,
              hasSyntheticBaseRow: false,
            };
          }
          return {
            ...rateSet,
            rows: [
              {
                label: 'Base Price',
                people: null,
                cells: [],
              },
            ],
            hasSyntheticBaseRow: true,
          };
        };

        this.flexibleGroups = (data.flexible_groups || []).map((group) => ({
          ...group,
          ratesets: (group.ratesets || []).map((rateSet) =>
            normalizeFlexibleRateSet(rateSet),
          ),
        }));
        this.fixedGroups = (data.fixed_groups || []).map((group) => ({
          ...group,
          ratesets: (group.ratesets || []).map((rateSet) =>
            this.normalizeFixedRateSet({
              ...rateSet,
              group_key: rateSet?.group_key || group?.key || null,
              type_key: rateSet?.type_key || group?.key || null,
            }),
          ),
        }));
        const normalizedFlexibleDisplayRows = (
          data.flexible_display_rows || []
        ).map((row) => {
          if (row.row_type !== 'rateset') {
            return row;
          }
          return {
            ...row,
            rate_set: normalizeFlexibleRateSet(row.rate_set),
          };
        });
        this.flexibleDisplayRows = this.withDerivedPriceBanners(
          normalizedFlexibleDisplayRows,
        );
        this.flexibleRatesets = this.flexibleGroups.flatMap((group) =>
          (group.ratesets || []).map((rateSet) =>
            normalizeFlexibleRateSet(rateSet),
          ),
        );
        this.fixedRatesets = this.fixedGroups.flatMap(
          (group) => group.ratesets || [],
        );

        if (!this.flexibleDisplayRows.length && this.flexibleGroups.length) {
          this.flexibleDisplayRows = this.withDerivedPriceBanners(
            this.buildFlexibleDisplayRowsFromGroups(),
          );
        }
        if (this.fixedGroups.length > 0) {
          this.fixedDisplayRows = this.withFixedTableRows(
            this.buildFixedDisplayRowsFromGroups(),
          );
        } else {
          this.fixedDisplayRows = [];
        }

        // Hard fallback: if grouped payload is missing but flat payload exists.
        if (
          !this.flexibleRatesets.length &&
          Array.isArray(data.flexible_ratesets)
        ) {
          this.flexibleRatesets = data.flexible_ratesets.map((rateSet) =>
            normalizeFlexibleRateSet(rateSet),
          );
        }
        if (!this.fixedRatesets.length && Array.isArray(data.fixed_ratesets)) {
          this.fixedRatesets = data.fixed_ratesets.map((rateSet) =>
            this.normalizeFixedRateSet(rateSet),
          );
          this.fixedDisplayRows = this.withFixedTableRows(
            this.buildFixedDisplayRowsFromGroups(),
          );
        }

        const incomingRateSetOptions = data.rate_set_options || [];
        if (this.selectedRateSetIds.length === 0) {
          this.allRateSetOptions = incomingRateSetOptions;
          this.rateSetOptions = incomingRateSetOptions;
        } else {
          if (this.allRateSetOptions.length === 0) {
            this.allRateSetOptions = incomingRateSetOptions;
          }
          this.rateSetOptions =
            this.allRateSetOptions.length > 0
              ? this.allRateSetOptions
              : incomingRateSetOptions;
        }
        this.pendingSelectedRateSetIds = [...this.selectedRateSetIds];
        this.renderVersion += 1;
        setTimeout(() => this.updateTableScrollIndicators(), 0);
      } catch (error) {
        if (requestId !== this.loadRequestId) {
          return;
        }
        console.error('Failed to load global pricing data', error);
        alert(error.message || 'Failed to load pricing data');
      } finally {
        if (requestId === this.loadRequestId) {
          this.loading = false;
        }
      }
    },
    trackCellChange(rateSet, row, cell, field, rawValue) {
      if (
        field === 'instant_book_enabled' &&
        !this.isInstantBookEnabledForCalendar()
      ) {
        return;
      }
      const boolFields = [
        'closed_to_arrival',
        'closed_to_departure',
        'instant_book_enabled',
        'is_blocked',
      ];
      const numericFields = ['price', 'min_length', 'max_length'];

      let value = rawValue;
      if (boolFields.includes(field)) {
        value = !!rawValue;
      } else if (numericFields.includes(field)) {
        value = parseNumeric(rawValue);
        // Price is NOT NULL in DB. Treat cleared/invalid values as 0.
        if (field === 'price' && value === null) {
          value = 0;
        } else if (field === 'price' && value > PRICE_MAX) {
          // Keep prior valid value instead of force-jumping while typing.
          return;
        }
      }

      cell[field] = value;
      if (!rateSet.bookable_by_date) {
        rateSet.bookable_by_date = {};
      }
      const liveBookable = this.getDateBookableFromRows(rateSet, cell.date);
      if (liveBookable !== null) {
        rateSet.bookable_by_date[cell.date] = liveBookable;
      }
      const key = `${rateSet.id}|${row.people || 'base'}|${cell.date}`;
      if (!this.changedCells[key]) {
        this.changedCells[key] = {
          rateSetId: rateSet.id,
          rateType: rateSet.rate_type,
          people: row.people,
          date: cell.date,
          rateId: cell.rate_id,
          fields: {},
        };
      }
      this.changedCells[key].fields[field] = value;
    },
    hasCellFieldChange(rateSet, row, date, field) {
      if (!rateSet || !row || !date || !field) {
        return false;
      }
      const key = `${rateSet.id}|${row.people || 'base'}|${date}`;
      return !!this.changedCells[key]?.fields?.hasOwnProperty(field);
    },
    async discardChanges() {
      if (!this.hasChanges) {
        return;
      }
      if (!window.confirm('Discard unsaved pricing changes?')) {
        return;
      }
      this.changedCells = {};
      await this.loadData();
    },
    async saveChanges() {
      try {
        const grouped = {};
        Object.values(this.changedCells).forEach((change) => {
          if (!grouped[change.rateSetId]) {
            grouped[change.rateSetId] = {
              rateType: change.rateType,
              updatesByRateId: {},
              createsByDate: {},
            };
          }
          const bucket = grouped[change.rateSetId];
          if (change.rateId) {
            if (!bucket.updatesByRateId[change.rateId]) {
              bucket.updatesByRateId[change.rateId] = {
                rate_id: change.rateId,
                fields: {},
              };
            }
            bucket.updatesByRateId[change.rateId].fields = {
              ...bucket.updatesByRateId[change.rateId].fields,
              ...change.fields,
            };
            return;
          }

          const createKey = `${change.date}|${change.people || 'base'}`;
          if (!bucket.createsByDate[createKey]) {
            bucket.createsByDate[createKey] = {
              date: change.date,
              people: change.people,
              fields: {},
            };
          }
          bucket.createsByDate[createKey].fields = {
            ...bucket.createsByDate[createKey].fields,
            ...change.fields,
          };
        });

        const csrfToken = getCSRFToken();
        for (const [rateSetId, payload] of Object.entries(grouped)) {
          const creates = Object.values(payload.createsByDate).filter(
            (entry) =>
              Object.prototype.hasOwnProperty.call(entry.fields, 'price') &&
              entry.fields.price !== null &&
              typeof entry.fields.price !== 'undefined',
          );
          const body = {
            rate_set_id: Number(rateSetId),
            rate_type: payload.rateType,
            updates: Object.values(payload.updatesByRateId),
            creates,
          };
          const response = await fetch('/rates/batch-update-rates/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken,
            },
            body: JSON.stringify(body),
          });
          const data = await response.json();
          if (!response.ok || !data.success) {
            throw new Error(
              data.message || `Failed to save rateset ${rateSetId}`,
            );
          }
        }

        this.changedCells = {};
        await this.loadData();
      } catch (error) {
        console.error('Failed saving pricing changes', error);
        alert(error.message || 'Failed to save pricing changes.');
      }
    },
    openBulkModal() {
      if (this.bulkSelection.length === 0) {
        return;
      }
      this.clearBulkDateRange();
      const bulkPriceCompatibility = this.getBulkPriceCompatibility();
      if (bulkPriceCompatibility.mode === 'occupancy') {
        const requiredLength = bulkPriceCompatibility.peoplePerUnit || 0;
        const existing = Array.isArray(this.bulkForm.prices)
          ? this.bulkForm.prices
          : [];
        this.bulkForm.prices = Array.from(
          { length: requiredLength },
          (_value, index) => existing[index] ?? '',
        );
      } else {
        this.bulkForm.prices = [];
      }
      this.bulkFormSubmitError = '';
      this.showBulkModal = true;
    },
    async submitBulkUpdate() {
      try {
        this.bulkFormSubmitError = '';
        if (this.bulkSelection.length === 0) {
          this.setBulkFormSubmitError(
            'Please select one or more listings/packages to update.',
          );
          return;
        }
        if (!this.bulkForm.start_date || !this.bulkForm.end_date) {
          this.setBulkFormSubmitError('Please select a valid date range.');
          return;
        }
        const canBulkEditPrice = this.canShowBulkPriceField();
        const bulkPriceCompatibility = this.getBulkPriceCompatibility();
        let bulkPrice = parseNumeric(this.bulkForm.price);
        let bulkPrices = [];
        const priceActive = canBulkEditPrice && this.bulkForm.price_active;
        if (priceActive && bulkPriceCompatibility.mode === 'occupancy') {
          const expectedPeopleCount = bulkPriceCompatibility.peoplePerUnit || 0;
          const rawPrices = Array.isArray(this.bulkForm.prices)
            ? this.bulkForm.prices
            : [];
          for (let index = 0; index < expectedPeopleCount; index += 1) {
            const parsedTierPrice = parseNumeric(rawPrices[index]);
            if (parsedTierPrice === null) {
              this.setBulkFormSubmitError(
                `Invalid price value for ${index + 1} ${
                  index + 1 === 1 ? 'person' : 'people'
                }.`,
              );
              return;
            }
            bulkPrices.push(clampPriceValue(parsedTierPrice));
          }
          this.bulkForm.prices = bulkPrices.map((entry) => String(entry));
          bulkPrice = null;
        } else {
          if (priceActive && this.bulkForm.price !== '' && bulkPrice === null) {
            this.setBulkFormSubmitError(
              'Invalid price value. Please enter a valid number.',
            );
            return;
          }
          if (priceActive && bulkPrice !== null) {
            bulkPrice = clampPriceValue(bulkPrice);
            this.bulkForm.price = String(bulkPrice);
          } else if (!priceActive) {
            bulkPrice = null;
          }
          bulkPrices = [];
        }
        const payload = {
          rateSetIds: this.bulkSelection,
          start_date: this.bulkForm.start_date,
          end_date: this.bulkForm.end_date,
          price: bulkPrice,
          prices: bulkPrices,
          people_influence_price: bulkPriceCompatibility.mode === 'occupancy',
          min_length: parseNumeric(this.bulkForm.min_length),
          max_length: parseNumeric(this.bulkForm.max_length),
          closed_to_arrival: this.bulkForm.closed_to_arrival || [],
          closed_to_departure: this.bulkForm.closed_to_departure || [],
          instant_book_enabled: this.bulkForm.instant_book_enabled || [],
          is_blocked:
            this.bulkForm.is_blocked === true ||
            this.bulkForm.is_blocked === 'true',
          price_active: priceActive,
          min_length_active: this.bulkForm.min_length_active,
          max_length_active: this.bulkForm.max_length_active,
          closed_to_arrival_active: this.bulkForm.closed_to_arrival_active,
          closed_to_departure_active: this.bulkForm.closed_to_departure_active,
          instant_book_enabled_active:
            this.bulkForm.instant_book_enabled_active,
          is_blocked_active: this.bulkForm.is_blocked_active,
        };

        const response = await fetch(
          '/rates/global-pricing-conditions-bulk-update/',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify(payload),
          },
        );
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          this.showBulkModal = false;
          this.setBulkUpdateStatus(
            'error',
            data.message || 'Bulk update failed.',
            false,
          );
          return;
        }
        this.showBulkModal = false;
        this.stopBulkStatusPolling();
        const queuedTasks = (data.tasks || []).map((entry) => entry.task_id);
        const skipped = data.skipped || [];

        if (!queuedTasks.length) {
          const skippedMessage = skipped.length
            ? ` ${skipped
                .slice(0, 3)
                .map((entry) => entry.reason)
                .join(' | ')}`
            : '';
          this.setBulkUpdateStatus(
            'error',
            `No updates were queued.${skippedMessage}`,
            false,
          );
          return;
        }

        this.setBulkUpdateStatus('info', 'Updating...', true);
        this.pollBulkUpdateTasks(queuedTasks);
      } catch (error) {
        console.error('Bulk update failed', error);
        this.showBulkModal = false;
        this.setBulkUpdateStatus(
          'error',
          error.message || 'Bulk update failed.',
          false,
        );
      }
    },
  };

  Object.defineProperties(store, Object.getOwnPropertyDescriptors(extension));
  return Alpine.store('calendarSettingsPricingConditionsStore', store);
}
